// Enhanced Strength Calculator with automatic thickness optimization
// Includes support structures, reinforcement calculations, and comprehensive analysis

import { logModuleInit } from '@/utils/invariant';
logModuleInit('EnhancedStrengthCalculator.ts');

import { MATERIALS, MaterialProperties, SupportType, SUPPORT_COEFFICIENTS, SAFETY_FACTORS } from './MaterialDatabase';

// Zabezpieczenie dla STANDARD_THICKNESSES
const STANDARD_THICKNESSES_SAFE: Record<string, number[]> = {};

console.log('[EnhancedStrengthCalculator] MATERIALS loaded:', typeof MATERIALS, Object.keys(MATERIALS || {}).length);

// Rozszerzone interfejsy
export interface EnhancedCalculationInput extends PlateCalculationInput {
  // Dodatkowe parametry dla zaawansowanych obliczeń
  environmentTemp?: number; // °C
  requiredLifetime?: number; // lata
  exposureType?: 'indoor' | 'outdoor' | 'industrial';
  dynamicLoad?: boolean; // obciążenie dynamiczne
  impactResistance?: boolean; // odporność na uderzenia
  chemicalExposure?: string[]; // narażenie na chemikalia
  maxAllowedDeflection?: number; // maksymalne dopuszczalne ugięcie mm
  budget?: number; // budżet PLN/m²
}

export interface PlateCalculationInput {
  material: string;
  thickness: number; // mm
  width: number; // mm
  length: number; // mm
  supportType: SupportType;
  loadType: 'uniform' | 'point' | 'distributed';
  load: number; // kg (uniform: kg/m², point: kg)
  loadPosition?: { x: number; y: number }; // dla obciążenia punktowego
  safetyFactor?: number;
}

export interface OptimalSolution {
  material: string;
  thickness: number;
  needsSupport: boolean;
  supportConfig?: SupportConfiguration;
  needsReinforcement: boolean;
  reinforcementConfig?: ReinforcementConfiguration;
  totalCost: number;
  weight: number;
  maxDeflection: number;
  maxStress: number;
  safetyMargin: number;
  lifeExpectancy: number;
  confidenceLevel: number; // 0-100%
}

export interface SupportConfiguration {
  type: 'beam' | 'frame' | 'grid' | 'central_support' | 'edge_reinforcement';
  material: string;
  profile: string;
  dimensions: {
    beamWidth?: number;
    beamHeight?: number;
    gridSpacing?: number;
    frameThickness?: number;
  };
  positions: Array<{ x: number; y: number; z: number }>;
  weight: number;
  cost: number;
  installationNotes: string[];
}

export interface ReinforcementConfiguration {
  type: 'ribs' | 'honeycomb' | 'corrugated' | 'sandwich' | 'composite';
  pattern: string;
  material: string;
  thickness: number;
  spacing: number;
  orientation: 'longitudinal' | 'transverse' | 'diagonal' | 'grid';
  weight: number;
  cost: number;
  manufacturingMethod: string;
}

export interface BeamCalculationInput {
  material: string;
  crossSection: {
    type: 'rectangular' | 'circular' | 'tube' | 'profile' | 'custom';
    dimensions: any;
  };
  length: number; // mm
  supportType: 'simply_supported' | 'cantilever' | 'fixed_both' | 'continuous';
  loads: Array<{
    type: 'point' | 'uniform' | 'moment';
    value: number;
    position?: number;
    startPos?: number;
    endPos?: number;
  }>;
  safetyFactor?: number;
  buckling?: boolean; // sprawdzenie wyboczenia
  torsion?: boolean; // skręcanie
}

export interface CalculationResult {
  maxDeflection: number; // mm
  maxStress: number; // MPa
  safetyFactor: number;
  percentOfMaxStress: number; // %
  isSafe: boolean;
  recommendations: string[];
  alternativeSolutions: AlternativeSolution[];
  optimalSolution?: OptimalSolution;
  visualizationData?: any;
  criticalPoints?: CriticalPoint[];
  fatigueLife?: number; // liczba cykli
  creepDeformation?: number; // pełzanie mm
}

export interface CriticalPoint {
  location: { x: number; y: number; z: number };
  stress: number;
  deflection: number;
  type: 'max_stress' | 'max_deflection' | 'stress_concentration' | 'buckling_risk';
  severity: 'low' | 'medium' | 'high' | 'critical';
}

export interface AlternativeSolution {
  description: string;
  material?: string;
  thickness?: number;
  additionalSupport?: string;
  estimatedCost?: number;
  weightReduction?: number;
  pros: string[];
  cons: string[];
  feasibility: number; // 0-100%
}

export class EnhancedStrengthCalculator {
  
  /**
   * Automatycznie dobierz optymalną grubość i konfigurację
   */
  static findOptimalConfiguration(
    dimensions: { width: number; length: number },
    maxLoad: number,
    requirements: {
      maxDeflection?: number;
      maxBudget?: number;
      preferredMaterials?: string[];
      supportType: SupportType;
      loadType: 'uniform' | 'point';
      environment?: 'indoor' | 'outdoor' | 'industrial';
    }
  ): OptimalSolution[] {
    const solutions: OptimalSolution[] = [];
    
    // Domyślne wymagania
    const maxAllowedDeflection = requirements.maxDeflection || Math.min(dimensions.width, dimensions.length) / 250;
    const preferredMaterials = requirements.preferredMaterials || Object.keys(MATERIALS);
    
    // Testuj różne konfiguracje
    for (const materialKey of preferredMaterials) {
      const material = MATERIALS[materialKey];
      if (!material) continue;
      
      // Sprawdź różne grubości
      const thicknesses = (STANDARD_THICKNESSES && STANDARD_THICKNESSES[materialKey] && Array.isArray(STANDARD_THICKNESSES[materialKey])) 
        ? STANDARD_THICKNESSES[materialKey] 
        : [3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30];
      
      console.log(`[EnhancedStrengthCalculator] Testing material ${materialKey} with thicknesses:`, thicknesses);
      
      for (const thickness of thicknesses) {
        // Podstawowe obliczenia
        const result = this.calculatePlateStrength({
          material: materialKey,
          thickness,
          width: dimensions.width,
          length: dimensions.length,
          supportType: requirements.supportType,
          loadType: requirements.loadType,
          load: maxLoad,
          safetyFactor: this.getRecommendedSafetyFactor(requirements.environment)
        });
        
        console.log(`[EnhancedStrengthCalculator] ${materialKey} ${thickness}mm: deflection=${result.maxDeflection}mm, stress=${result.maxStress}MPa, safe=${result.isSafe}`);
        
        // Sprawdź czy spełnia wymagania
        if (result.maxDeflection <= maxAllowedDeflection && result.isSafe) {
          // Oblicz koszt
          const area = (dimensions.width * dimensions.length) / 1000000; // m²
          const volume = area * (thickness / 1000); // m³
          const weight = volume * material.density; // kg
          const materialCost = weight * material.pricePerKg;
          
          // Czy potrzebne wsparcie?
          let needsSupport = false;
          let supportConfig: SupportConfiguration | undefined;
          
          if (result.maxDeflection > maxAllowedDeflection * 0.7 || result.percentOfMaxStress > 70) {
            supportConfig = this.calculateOptimalSupport(
              dimensions,
              thickness,
              materialKey,
              maxLoad,
              requirements.supportType
            );
            needsSupport = true;
          }
          
          // Czy potrzebne wzmocnienie?
          let needsReinforcement = false;
          let reinforcementConfig: ReinforcementConfiguration | undefined;
          
          if (result.percentOfMaxStress > 50 && thickness < 10) {
            reinforcementConfig = this.calculateOptimalReinforcement(
              dimensions,
              thickness,
              materialKey,
              maxLoad
            );
            needsReinforcement = true;
          }
          
          const totalCost = materialCost + 
            (supportConfig?.cost || 0) + 
            (reinforcementConfig?.cost || 0);
          
          // Dodaj rozwiązanie jeśli mieści się w budżecie
          if (!requirements.maxBudget || totalCost <= requirements.maxBudget) {
            solutions.push({
              material: materialKey,
              thickness,
              needsSupport,
              supportConfig,
              needsReinforcement,
              reinforcementConfig,
              totalCost: Math.round(totalCost),
              weight: Math.round(weight * 10) / 10,
              maxDeflection: result.maxDeflection,
              maxStress: result.maxStress,
              safetyMargin: (100 - result.percentOfMaxStress),
              lifeExpectancy: this.estimateLifeExpectancy(material, result.percentOfMaxStress, requirements.environment),
              confidenceLevel: this.calculateConfidenceLevel(result, material)
            });
          }
        }
      }
    }
    
    // Sortuj po optymalności (koszt/wydajność)
    solutions.sort((a, b) => {
      // Priorytet: bezpieczeństwo > koszt > waga
      const scoreA = a.safetyMargin * 2 - a.totalCost / 100 - a.weight / 10;
      const scoreB = b.safetyMargin * 2 - b.totalCost / 100 - b.weight / 10;
      return scoreB - scoreA;
    });
    
    // Zwróć top 5 rozwiązań (ale sprawdź czy są jakieś rozwiązania)
    return solutions && Array.isArray(solutions) && solutions.length > 0 
      ? solutions.slice(0, 5) 
      : [];
  }
  
  /**
   * Oblicz optymalną konfigurację wsparcia
   */
  static calculateOptimalSupport(
    dimensions: { width: number; length: number },
    thickness: number,
    material: string,
    load: number,
    supportType: SupportType
  ): SupportConfiguration {
    const mat = MATERIALS[material];
    const area = (dimensions.width * dimensions.length) / 1000000; // m²
    
    // Sprawdź czy materiał jest przezroczysty
    const isTransparent = mat.transmittance > 80;
    
    // Analiza typu wsparcia
    let config: SupportConfiguration;
    
    // Dla materiałów przezroczystych - preferuj rozwiązania z tego samego materiału
    if (isTransparent) {
      // Dla dużych powierzchni > 1m² - żebrowanie z tego samego materiału
      if (area > 1) {
        config = {
          type: 'grid',
          material: material, // Ten sam materiał!
          profile: 'ribs',
          dimensions: {
            beamWidth: thickness * 2,
            beamHeight: 40,
            gridSpacing: Math.min(250, Math.max(dimensions.width, dimensions.length) / 5)
          },
          positions: this.generateGridPositions(dimensions, 250),
          weight: area * thickness * 0.2 * mat.density / 1000,
          cost: area * 80, // Koszt klejenia i materiału
          installationNotes: [
            `Żebrowanie z ${mat.displayName} ${thickness}mm`,
            `Wysokość żeber 40mm`,
            `Klejenie rozpuszczalnikowe lub ultradźwiękowe`,
            `Zachowana pełna przezroczystość`,
            `Rozstaw żeber co 250mm`
          ]
        };
      }
      // Dla średnich powierzchni - wzmocnienia krawędziowe
      else if (area > 0.5) {
        config = {
          type: 'edge_reinforcement',
          material: material,
          profile: 'thicker_edge',
          dimensions: {
            frameThickness: thickness * 1.5
          },
          positions: [
            { x: 0, y: 0, z: 0 },
            { x: dimensions.width, y: 0, z: 0 },
            { x: dimensions.width, y: dimensions.length, z: 0 },
            { x: 0, y: dimensions.length, z: 0 }
          ],
          weight: (dimensions.width + dimensions.length) * 0.05 * mat.density / 1000,
          cost: 60,
          installationNotes: [
            `Pogrubienie krawędzi do ${thickness * 1.5}mm`,
            `Klejenie pasmowe z tego samego materiału`,
            `Estetyczne wykończenie`,
            `Brak widocznych elementów obcych`
          ]
        };
      }
      // Dla małych powierzchni - dodatkowa warstwa
      else {
        config = {
          type: 'central_support',
          material: material,
          profile: 'double_layer',
          dimensions: {
            beamWidth: dimensions.width * 0.3,
            beamHeight: thickness
          },
          positions: [
            { x: dimensions.width / 2, y: dimensions.length / 2, z: -thickness }
          ],
          weight: area * 0.3 * thickness * mat.density / 1000,
          cost: 40,
          installationNotes: [
            `Podwójna warstwa w środkowej części`,
            `Szerokość wzmocnienia ${Math.round(dimensions.width * 0.3)}mm`,
            `Klejone fabrycznie`,
            `Niewidoczne od góry`
          ]
        };
      }
    }
    // Dla materiałów nieprzezroczystych - można użyć innych rozwiązań
    else {
      // Dla dużych powierzchni > 1m²
      if (area > 1) {
        // Kratownica z PVC lub drewna
        config = {
          type: 'grid',
          material: 'pvc_foam_10mm',
          profile: 'support_grid',
          dimensions: {
            beamWidth: 50,
            beamHeight: 50,
            gridSpacing: 300
          },
          positions: this.generateGridPositions(dimensions, 300),
          weight: area * 2,
          cost: area * 40,
          installationNotes: [
            `Kratownica z PVC spienionego 10mm`,
            `Wysokość belek 50mm`,
            `Klejenie na montażu`,
            `Lekka konstrukcja`
          ]
        };
      }
      // Dla średnich powierzchni
      else if (area > 0.5) {
        config = {
          type: 'frame',
          material: 'mdf_18mm',
          profile: 'wood_frame',
          dimensions: {
            frameThickness: 40
          },
          positions: [
            { x: 0, y: 0, z: 0 },
            { x: dimensions.width, y: 0, z: 0 },
            { x: dimensions.width, y: dimensions.length, z: 0 },
            { x: 0, y: dimensions.length, z: 0 }
          ],
          weight: (dimensions.width + dimensions.length) * 2 * 0.04 * 0.75,
          cost: 30,
          installationNotes: [
            'Rama z MDF 18mm',
            'Szerokość ramy 40mm',
            'Montaż na klej i wkręty',
            'Możliwość lakierowania'
          ]
        };
      }
      // Dla małych powierzchni
      else {
        config = {
          type: 'central_support',
          material: material,
          profile: 'thicker_material',
          dimensions: {
            beamWidth: 0,
            beamHeight: 0
          },
          positions: [],
          weight: 0,
          cost: 0,
          installationNotes: [
            `Zalecane zwiększenie grubości materiału`,
            `Brak dodatkowych wzmocnień`
          ]
        };
      }
    }
    
    return config;
  }
  
  /**
   * Oblicz optymalne wzmocnienie
   */
  static calculateOptimalReinforcement(
    dimensions: { width: number; length: number },
    thickness: number,
    material: string,
    load: number
  ): ReinforcementConfiguration {
    const area = (dimensions.width * dimensions.length) / 1000000;
    
    // Dla obciążeń > 100 kg/m²
    if (load > 100) {
      return {
        type: 'ribs',
        pattern: 'grid',
        material: material,
        thickness: thickness,
        spacing: 150,
        orientation: 'grid',
        weight: area * thickness * 0.15 * MATERIALS[material].density / 1000,
        cost: area * 50, // 50 PLN/m² za żebrowanie
        manufacturingMethod: 'Klejenie ultradźwiękowe lub chemiczne'
      };
    }
    // Dla obciążeń 50-100 kg/m²
    else if (load > 50) {
      return {
        type: 'ribs',
        pattern: 'parallel',
        material: material,
        thickness: thickness * 0.8,
        spacing: 200,
        orientation: 'longitudinal',
        weight: area * thickness * 0.1 * MATERIALS[material].density / 1000,
        cost: area * 30,
        manufacturingMethod: 'Klejenie rozpuszczalnikowe'
      };
    }
    // Dla lekkich obciążeń
    else {
      return {
        type: 'corrugated',
        pattern: 'wave',
        material: material,
        thickness: thickness * 0.5,
        spacing: 50,
        orientation: 'transverse',
        weight: area * thickness * 0.05 * MATERIALS[material].density / 1000,
        cost: area * 20,
        manufacturingMethod: 'Termoformowanie'
      };
    }
  }
  
  /**
   * Generuj pozycje siatki wsporczej
   */
  private static generateGridPositions(
    dimensions: { width: number; length: number },
    spacing: number
  ): Array<{ x: number; y: number; z: number }> {
    const positions: Array<{ x: number; y: number; z: number }> = [];
    
    for (let x = 0; x <= dimensions.width; x += spacing) {
      for (let y = 0; y <= dimensions.length; y += spacing) {
        positions.push({ x, y, z: 0 });
      }
    }
    
    return positions;
  }
  
  /**
   * Oblicz zalecaną wartość współczynnika bezpieczeństwa
   */
  private static getRecommendedSafetyFactor(environment?: string): number {
    switch (environment) {
      case 'outdoor':
        return SAFETY_FACTORS.public;
      case 'industrial':
        return SAFETY_FACTORS.industrial;
      case 'indoor':
      default:
        return SAFETY_FACTORS.display;
    }
  }
  
  /**
   * Oszacuj żywotność konstrukcji
   */
  private static estimateLifeExpectancy(
    material: MaterialProperties,
    stressPercent: number,
    environment?: string
  ): number {
    let baseLife = 20; // lat
    
    // Redukcja ze względu na obciążenie
    if (stressPercent > 80) baseLife *= 0.5;
    else if (stressPercent > 60) baseLife *= 0.7;
    else if (stressPercent > 40) baseLife *= 0.85;
    
    // Redukcja ze względu na środowisko
    if (environment === 'outdoor' && !material.uvResistance) baseLife *= 0.5;
    if (environment === 'industrial') baseLife *= 0.7;
    
    // Bonus za wysoką jakość materiału
    if (material.chemicalResistance === 'high') baseLife *= 1.2;
    
    return Math.round(baseLife);
  }
  
  /**
   * Oblicz poziom pewności obliczeń
   */
  private static calculateConfidenceLevel(
    result: CalculationResult,
    material: MaterialProperties
  ): number {
    let confidence = 95; // Bazowy poziom
    
    // Redukcja przy wysokich naprężeniach
    if (result.percentOfMaxStress > 90) confidence -= 20;
    else if (result.percentOfMaxStress > 70) confidence -= 10;
    else if (result.percentOfMaxStress > 50) confidence -= 5;
    
    // Redukcja przy dużych ugięciach
    if (result.maxDeflection > 10) confidence -= 10;
    else if (result.maxDeflection > 5) confidence -= 5;
    
    // Bonus za sprawdzone materiały
    if (material.name.includes('pmma') || material.name.includes('polycarbonate')) {
      confidence += 5;
    }
    
    return Math.max(50, Math.min(100, confidence));
  }
  
  /**
   * Oblicz wytrzymałość płyty - metoda podstawowa
   */
  static calculatePlateStrength(input: PlateCalculationInput): CalculationResult {
    const material = MATERIALS[input.material];
    if (!material) {
      throw new Error(`Nieznany materiał: ${input.material}`);
    }

    const safetyFactor = input.safetyFactor || SAFETY_FACTORS.display;
    
    // Konwersje jednostek
    const thickness_m = input.thickness / 1000;
    const width_m = input.width / 1000;
    const length_m = input.length / 1000;
    
    // Moduł sprężystości w Pa
    const E = material.modulusOfElasticity * 1e6;
    
    // Współczynnik Poissona
    const nu = material.poissonsRatio || 0.3;
    
    // Sztywność płytowa (plate rigidity) - KLUCZOWE DLA PŁYT!
    const D = (E * Math.pow(thickness_m, 3)) / (12 * (1 - Math.pow(nu, 2)));
    
    let maxDeflection = 0;
    let maxStress = 0;
    
    if (input.loadType === 'uniform') {
      // Obciążenie równomierne w N/m²
      const q = (input.load * 9.81); // kg/m² -> N/m²
      
      if (input.supportType === 'simply_supported') {
        // Belka - używamy klasycznych wzorów belkowych
        const I = (width_m * Math.pow(thickness_m, 3)) / 12;
        maxDeflection = (5 * q * width_m * Math.pow(length_m, 4)) / (384 * E * I) * 1000;
        maxStress = (q * width_m * Math.pow(length_m, 2)) / (8 * (width_m * Math.pow(thickness_m, 2) / 6)) / 1e6;
      } else if (input.supportType === 'four_sides') {
        // Płyta podparta na 4 bokach - teoria płyt Kirchhoffa
        const a = Math.min(width_m, length_m);
        const b = Math.max(width_m, length_m);
        const ratio = a / b;
        
        // Współczynniki dla ugięcia i naprężenia (tablice Timoshenko)
        const alpha = 0.00406 + 0.00896 * ratio - 0.00316 * Math.pow(ratio, 2);
        const beta = 0.287 + 0.196 * ratio - 0.103 * Math.pow(ratio, 2);
        
        // Ugięcie maksymalne - zależy od D (czyli od thickness^3!)
        maxDeflection = (alpha * q * Math.pow(a, 4)) / D * 1000;
        
        // Naprężenie maksymalne - zależy od thickness^2
        maxStress = (beta * q * Math.pow(a, 2)) / Math.pow(thickness_m, 2) / 1e6;
      } else if (input.supportType === 'cantilever') {
        // Wspornik
        const I = (width_m * Math.pow(thickness_m, 3)) / 12;
        maxDeflection = (q * width_m * Math.pow(length_m, 4)) / (8 * E * I) * 1000;
        maxStress = (3 * q * width_m * Math.pow(length_m, 2)) / (2 * width_m * thickness_m) / 1e6;
      } else if (input.supportType === 'three_sides') {
        // Płyta podparta na 3 bokach
        const coefficient = SUPPORT_COEFFICIENTS[input.supportType] || 0.013;
        maxDeflection = (coefficient * q * Math.pow(length_m, 4)) / D * 1000;
        maxStress = (0.5 * q * Math.pow(length_m, 2)) / Math.pow(thickness_m, 2) / 1e6;
      } else if (input.supportType === 'fixed_both') {
        // Utwierdzona na obu końcach
        const I = (width_m * Math.pow(thickness_m, 3)) / 12;
        maxDeflection = (q * width_m * Math.pow(length_m, 4)) / (384 * E * I) * 1000;
        maxStress = (q * width_m * Math.pow(length_m, 2)) / (12 * (width_m * Math.pow(thickness_m, 2) / 6)) / 1e6;
      } else if (input.supportType === 'continuous') {
        // Podparcie ciągłe
        maxDeflection = (0.00126 * q * Math.pow(length_m, 4)) / D * 1000;
        maxStress = (0.0513 * q * Math.pow(length_m, 2)) / Math.pow(thickness_m, 2) / 1e6;
      } else if (input.supportType === 'point_supports') {
        // Podpory punktowe - zakładamy 4 narożniki
        maxDeflection = (0.0284 * q * Math.pow(Math.min(width_m, length_m), 4)) / D * 1000;
        maxStress = (0.338 * q * Math.pow(Math.min(width_m, length_m), 2)) / Math.pow(thickness_m, 2) / 1e6;
      }
    } else if (input.loadType === 'point') {
      // Obciążenie punktowe w N
      const P = input.load * 9.81;
      
      if (input.supportType === 'simply_supported') {
        // Belka z obciążeniem punktowym
        const I = (width_m * Math.pow(thickness_m, 3)) / 12;
        maxDeflection = (P * Math.pow(length_m, 3)) / (48 * E * I) * 1000;
        maxStress = (P * length_m) / (4 * (width_m * Math.pow(thickness_m, 2) / 6)) / 1e6;
      } else if (input.supportType === 'four_sides') {
        // Płyta z obciążeniem punktowym
        const a = Math.min(width_m, length_m);
        maxDeflection = (P * Math.pow(a, 2)) / (16 * Math.PI * D) * 1000;
        maxStress = (0.6 * P) / Math.pow(thickness_m, 2) / 1e6;
      } else if (input.supportType === 'cantilever') {
        // Wspornik z obciążeniem punktowym
        const I = (width_m * Math.pow(thickness_m, 3)) / 12;
        maxDeflection = (P * Math.pow(length_m, 3)) / (3 * E * I) * 1000;
        maxStress = (P * length_m) / ((width_m * Math.pow(thickness_m, 2)) / 6) / 1e6;
      } else if (input.supportType === 'three_sides') {
        // Płyta na 3 bokach z obciążeniem punktowym
        maxDeflection = (0.0056 * P * Math.pow(length_m, 2)) / D * 1000;
        maxStress = (1.24 * P) / Math.pow(thickness_m, 2) / 1e6;
      } else if (input.supportType === 'fixed_both') {
        // Utwierdzona z obciążeniem punktowym
        const I = (width_m * Math.pow(thickness_m, 3)) / 12;
        maxDeflection = (P * Math.pow(length_m, 3)) / (192 * E * I) * 1000;
        maxStress = (P * length_m) / (8 * (width_m * Math.pow(thickness_m, 2) / 6)) / 1e6;
      }
    }
    
    const percentOfMaxStress = (maxStress / material.flexuralStrength) * 100;
    const isSafe = percentOfMaxStress < (100 / safetyFactor);
    
    // Znajdź punkty krytyczne
    const criticalPoints = this.findCriticalPoints(input, maxDeflection, maxStress);
    
    // Generuj rekomendacje
    const recommendations = this.generateRecommendations(
      input, 
      maxDeflection, 
      maxStress, 
      material, 
      percentOfMaxStress
    );
    
    // Znajdź alternatywne rozwiązania
    const alternativeSolutions = this.findAlternativeSolutions(
      input,
      maxDeflection,
      maxStress
    );
    
    return {
      maxDeflection: Math.round(maxDeflection * 100) / 100,
      maxStress: Math.round(maxStress * 10) / 10,
      safetyFactor: safetyFactor,
      percentOfMaxStress: Math.round(percentOfMaxStress),
      isSafe,
      recommendations,
      alternativeSolutions,
      visualizationData: this.generateVisualizationData(input, maxDeflection, maxStress),
      criticalPoints
    };
  }
  
  /**
   * Znajdź punkty krytyczne konstrukcji
   */
  private static findCriticalPoints(
    input: PlateCalculationInput,
    maxDeflection: number,
    maxStress: number
  ): CriticalPoint[] {
    const points: CriticalPoint[] = [];
    
    // Punkt maksymalnego ugięcia
    if (input.supportType === 'simply_supported' || input.supportType === 'four_sides') {
      points.push({
        location: { x: input.width / 2, y: input.length / 2, z: -maxDeflection },
        stress: maxStress * 0.8,
        deflection: maxDeflection,
        type: 'max_deflection',
        severity: maxDeflection > 10 ? 'high' : maxDeflection > 5 ? 'medium' : 'low'
      });
    }
    
    // Punkt maksymalnych naprężeń
    if (input.supportType === 'cantilever') {
      points.push({
        location: { x: 0, y: 0, z: 0 },
        stress: maxStress,
        deflection: 0,
        type: 'max_stress',
        severity: maxStress > MATERIALS[input.material].flexuralStrength * 0.8 ? 'critical' : 'medium'
      });
    }
    
    // Koncentracje naprężeń w narożach
    if (input.supportType === 'four_sides') {
      const cornerStress = maxStress * 1.5; // Współczynnik koncentracji
      [
        { x: 0, y: 0 },
        { x: input.width, y: 0 },
        { x: input.width, y: input.length },
        { x: 0, y: input.length }
      ].forEach(corner => {
        points.push({
          location: { ...corner, z: 0 },
          stress: cornerStress,
          deflection: 0,
          type: 'stress_concentration',
          severity: cornerStress > MATERIALS[input.material].flexuralStrength ? 'high' : 'medium'
        });
      });
    }
    
    return points;
  }
  
  /**
   * Generuj rekomendacje
   */
  private static generateRecommendations(
    input: PlateCalculationInput,
    deflection: number,
    stress: number,
    material: MaterialProperties,
    percentStress: number
  ): string[] {
    const recommendations: string[] = [];
    
    const maxAllowedDeflection = Math.min(input.width, input.length) / 250;
    
    if (deflection > maxAllowedDeflection) {
      recommendations.push(
        `⚠️ Ugięcie ${deflection.toFixed(1)}mm przekracza dopuszczalne ${maxAllowedDeflection.toFixed(1)}mm (L/250)`
      );
      
      // Konkretne rekomendacje
      const thickerOption = Math.ceil(input.thickness * Math.pow(deflection / maxAllowedDeflection, 0.33));
      recommendations.push(
        `💡 Zwiększ grubość do ${thickerOption}mm lub dodaj podpory co ${Math.round(Math.min(input.width, input.length) / 3)}mm`
      );
    }
    
    if (percentStress > 80) {
      recommendations.push(
        `⚠️ Materiał pracuje na ${percentStress}% swojej wytrzymałości - ryzyko pęknięcia`
      );
      if (percentStress > 100) {
        recommendations.push(
          `❌ PRZEKROCZONA WYTRZYMAŁOŚĆ! Natychmiastowa zmiana konstrukcji wymagana!`
        );
      }
    } else if (percentStress < 30) {
      recommendations.push(
        `✅ Materiał wykorzystany tylko w ${percentStress}% - możliwa redukcja grubości do ${Math.floor(input.thickness * 0.7)}mm`
      );
    }
    
    // Rekomendacje środowiskowe
    if (material.maxServiceTemp < 60) {
      recommendations.push(
        `💡 Materiał nieodporny na wysokie temperatury - unikaj ekspozycji na słońce`
      );
    }
    
    if (!material.uvResistance) {
      recommendations.push(
        `💡 Rozważ dodanie powłoki UV lub wybór materiału z ochroną UV`
      );
    }
    
    // Rekomendacje montażowe
    if (deflection > maxAllowedDeflection * 0.7) {
      if (material.transmittance > 80) {
        // Dla materiałów przezroczystych
        recommendations.push(
          `💡 Dodaj żebra z tego samego materiału (${material.displayName}) w rozstawie ${Math.round(Math.min(input.width, input.length) / 4)}mm`
        );
        recommendations.push(
          `💡 Alternatywnie: zastosuj pogrubienie krawędzi lub podwójną warstwę w środku`
        );
      } else {
        // Dla materiałów nieprzezroczystych
        recommendations.push(
          `💡 Dodaj ramę z PVC spienionego lub MDF`
        );
        recommendations.push(
          `💡 Możliwe żebrowanie z tego samego materiału co ${Math.round(Math.min(input.width, input.length) / 4)}mm`
        );
      }
    }
    
    if (input.loadType === 'point' && input.load > 50) {
      recommendations.push(
        `💡 Rozłóż obciążenie używając płyty rozdzielczej min. 200x200mm`
      );
    }
    
    return recommendations;
  }
  
  /**
   * Znajdź alternatywne rozwiązania
   */
  private static findAlternativeSolutions(
    input: PlateCalculationInput,
    currentDeflection: number,
    currentStress: number
  ): AlternativeSolution[] {
    const solutions: AlternativeSolution[] = [];
    const currentMaterial = MATERIALS[input.material];
    
    // 1. Grubszy materiał
    if (currentStress > currentMaterial.flexuralStrength * 0.5) {
      const newThickness = Math.ceil(input.thickness * 1.5);
      solutions.push({
        description: `Zwiększ grubość do ${newThickness}mm`,
        thickness: newThickness,
        estimatedCost: 150,
        weightReduction: -50,
        pros: [
          'Prosta modyfikacja',
          'Nie wymaga dodatkowych elementów',
          'Zachowanie estetyki'
        ],
        cons: [
          'Wyższy koszt materiału',
          'Większa waga',
          'Możliwe problemy z obróbką'
        ],
        feasibility: 95
      });
    }
    
    // 2. Mocniejszy materiał
    const strongerMaterials = Object.entries(MATERIALS)
      .filter(([key, mat]) => mat.flexuralStrength > currentMaterial.flexuralStrength * 1.2)
      .slice(0, 3);
    
    strongerMaterials.forEach(([key, mat]) => {
      solutions.push({
        description: `Użyj ${mat.displayName}`,
        material: key,
        estimatedCost: Math.round((mat.pricePerKg / currentMaterial.pricePerKg) * 100),
        weightReduction: Math.round(((mat.density - currentMaterial.density) / currentMaterial.density) * 100),
        pros: [
          `Wytrzymałość ${Math.round(mat.flexuralStrength / currentMaterial.flexuralStrength * 100)}% wyższa`,
          mat.uvResistance ? 'Odporność UV' : null,
          mat.chemicalResistance === 'high' ? 'Wysoka odporność chemiczna' : null
        ].filter(Boolean) as string[],
        cons: [
          mat.pricePerKg > currentMaterial.pricePerKg ? 'Wyższy koszt' : null,
          mat.density > currentMaterial.density ? 'Większa waga' : null
        ].filter(Boolean) as string[],
        feasibility: 90
      });
    });
    
    // 3. Dodatkowe podpory
    if (input.supportType === 'simply_supported' || input.supportType === 'four_sides') {
      solutions.push({
        description: 'Dodaj centralną podporę',
        additionalSupport: 'central_beam',
        estimatedCost: 120,
        weightReduction: 5,
        pros: [
          'Redukcja ugięcia o ~75%',
          'Możliwość użycia cieńszego materiału',
          'Łatwy montaż'
        ],
        cons: [
          'Widoczny element konstrukcyjny',
          'Ograniczenie przestrzeni pod spodem'
        ],
        feasibility: 85
      });
    }
    
    // 4. Konstrukcja sandwich
    solutions.push({
      description: 'Konstrukcja warstwowa (sandwich)',
      additionalSupport: 'sandwich_panel',
      estimatedCost: 200,
      weightReduction: -20,
      pros: [
        'Bardzo wysoka sztywność',
        'Dobra izolacja termiczna',
        'Tłumienie drgań'
      ],
      cons: [
        'Skomplikowana produkcja',
        'Wyższy koszt',
        'Grubsza konstrukcja'
      ],
      feasibility: 70
    });
    
    // 5. Wzmocnienie z tego samego materiału
    if (currentMaterial.transmittance > 80) {
      // Dla materiałów przezroczystych
      solutions.push({
        description: 'Żebrowanie z tego samego materiału',
        additionalSupport: 'transparent_ribs',
        estimatedCost: 160,
        weightReduction: -15,
        pros: [
          'Zachowana przezroczystość',
          'Estetyczny wygląd',
          'Jednolity materiał',
          'Brak elementów metalowych'
        ],
        cons: [
          'Pracochłonne klejenie',
          'Wyższy koszt wykonania'
        ],
        feasibility: 95
      });
    } else {
      // Dla materiałów nieprzezroczystych - rama z PVC lub MDF
      solutions.push({
        description: 'Rama z PVC spienionego',
        additionalSupport: 'pvc_frame',
        estimatedCost: 120,
        weightReduction: 5,
        pros: [
          'Lekka konstrukcja',
          'Łatwa obróbka',
          'Tania',
          'Odporna na wilgoć'
        ],
        cons: [
          'Widoczna rama',
          'Mniejsza sztywność niż metal'
        ],
        feasibility: 90
      });
    }
    
    return solutions.slice(0, 4);
  }
  
  /**
   * Generuj dane do wizualizacji
   */
  private static generateVisualizationData(
    input: PlateCalculationInput,
    deflection: number,
    stress: number
  ): any {
    const gridSize = 20;
    const points: number[][] = [];
    
    for (let i = 0; i <= gridSize; i++) {
      const row: number[] = [];
      for (let j = 0; j <= gridSize; j++) {
        const x = (i / gridSize) * input.width;
        const y = (j / gridSize) * input.length;
        
        let z = 0;
        if (input.supportType === 'simply_supported') {
          z = deflection * Math.sin((Math.PI * i) / gridSize) * Math.sin((Math.PI * j) / gridSize);
        } else if (input.supportType === 'four_sides') {
          const distFromCenterX = Math.abs(i - gridSize/2) / (gridSize/2);
          const distFromCenterY = Math.abs(j - gridSize/2) / (gridSize/2);
          z = deflection * (1 - distFromCenterX) * (1 - distFromCenterY);
        } else if (input.supportType === 'cantilever') {
          z = deflection * Math.pow(i / gridSize, 2);
        }
        
        row.push(z);
      }
      points.push(row);
    }
    
    return {
      deflectionGrid: points,
      maxDeflectionPoint: { x: input.width / 2, y: input.length / 2, z: deflection },
      stressDistribution: this.calculateStressDistribution(input, stress),
      colorMap: this.generateColorMap(stress, MATERIALS[input.material].flexuralStrength)
    };
  }
  
  private static calculateStressDistribution(
    input: PlateCalculationInput,
    maxStress: number
  ): number[][] {
    const grid: number[][] = [];
    const gridSize = 10;
    
    for (let i = 0; i <= gridSize; i++) {
      const row: number[] = [];
      for (let j = 0; j <= gridSize; j++) {
        let stress = maxStress;
        
        if (input.supportType === 'simply_supported') {
          const distFromCenter = Math.sqrt(
            Math.pow(i - gridSize/2, 2) + Math.pow(j - gridSize/2, 2)
          ) / (gridSize/2);
          stress = maxStress * (1 - distFromCenter * 0.5);
        } else if (input.supportType === 'cantilever') {
          stress = maxStress * (i / gridSize);
        }
        
        row.push(stress);
      }
      grid.push(row);
    }
    
    return grid;
  }
  
  private static generateColorMap(stress: number, maxAllowedStress: number): any {
    const ratio = stress / maxAllowedStress;
    
    return {
      safe: ratio < 0.5 ? '#00ff00' : ratio < 0.8 ? '#ffff00' : '#ff0000',
      gradient: [
        { value: 0, color: '#0000ff' },
        { value: 0.25, color: '#00ff00' },
        { value: 0.5, color: '#ffff00' },
        { value: 0.75, color: '#ff8800' },
        { value: 1, color: '#ff0000' }
      ]
    };
  }
}

// Eksport standardowych grubości z zabezpieczeniem
export const STANDARD_THICKNESSES: Record<string, number[]> = {
  pmma_xt: [2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30],
  pmma_gs: [2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30, 40],
  petg: [1, 2, 3, 4, 5, 6, 8, 10, 12],
  polycarbonate: [2, 3, 4, 5, 6, 8, 10, 12, 15],
  hips: [1, 1.5, 2, 3, 4, 5],
  pvc_foam_3mm: [3],
  pvc_foam_5mm: [5],
  pvc_foam_10mm: [10],
  dibond_3mm: [3],
  glass_4mm: [4],
  glass_tempered_4mm: [4, 5, 6, 8, 10, 12],
  mdf_18mm: [3, 6, 10, 12, 16, 18, 22, 25],
  aluminum_6061: [1, 2, 3, 4, 5, 6, 8, 10]
};

// Funkcja pomocnicza do bezpiecznego dostępu
export const getStandardThicknesses = (material: string): number[] => {
  if (STANDARD_THICKNESSES && STANDARD_THICKNESSES[material] && Array.isArray(STANDARD_THICKNESSES[material])) {
    return STANDARD_THICKNESSES[material];
  }
  return [3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30]; // Domyślne grubości
};
